// Finds defined terms and references in clause text: case-folded, dash- and space-insensitive, plural-aware leftmost-longest matching at word boundaries, so marks never overlap and a capitalised term only matches capitalised text.
import type { DefinitionRef, ReferenceRef } from "@/api/__generated__";

export type TermMark =
  | { kind: "definition"; start: number; end: number; text: string; definition: DefinitionRef }
  | { kind: "reference"; start: number; end: number; text: string; references: ReferenceRef[] };

interface FoldedText {
  folded: string;
  starts: number[];
  ends: number[];
}

type Candidate =
  | { kind: "definition"; form: string; capitalised: boolean; definition: DefinitionRef }
  | { kind: "reference"; form: string; references: ReferenceRef[] };

const DASHES = /[-‐‑‒–—―]/u;
const SPACE = /\s/u;
const WORD_CHAR = /[\p{L}\p{N}]/u;
const OPTIONAL_PLURAL = /\(s\)$/iu;
const UPPERCASE_START = /^\p{Lu}/u;

function foldChar(char: string): string {
  if (DASHES.test(char) || SPACE.test(char)) {
    return " ";
  }
  return char.toLowerCase();
}

export function foldText(text: string): FoldedText {
  const units: string[] = [];
  const starts: number[] = [];
  const ends: number[] = [];
  let index = 0;
  for (const char of text) {
    const folded = foldChar(char);
    const end = index + char.length;
    if (folded === " " && units.at(-1) === " ") {
      ends[ends.length - 1] = end;
    } else {
      for (const unit of folded) {
        units.push(unit);
        starts.push(index);
        ends.push(end);
      }
    }
    index = end;
  }
  return { folded: units.join(""), starts, ends };
}

export function foldKey(text: string): string {
  return foldText(text.trim().replace(OPTIONAL_PLURAL, "")).folded.trim();
}

export function termForms(key: string): string[] {
  const forms = [key, `${key}s`, `${key}es`];
  if (key.endsWith("y")) {
    forms.push(`${key.slice(0, -1)}ies`);
  }
  return forms;
}

function isWordChar(char: string | undefined): boolean {
  return char !== undefined && WORD_CHAR.test(char);
}

function leadingWord(text: string, from: number): string {
  let end = from;
  while (end < text.length && isWordChar(text[end])) {
    end += 1;
  }
  return text.slice(from, end);
}

function referenceTexts(reference: ReferenceRef): string[] {
  return [reference.raw_text, reference.number, reference.designation].filter(
    (value): value is string => typeof value === "string" && value.trim().length > 0,
  );
}

function candidates(definitions: readonly DefinitionRef[], references: readonly ReferenceRef[]): Candidate[] {
  const list: Candidate[] = [];
  const seenDefinitions = new Set<string>();
  for (const definition of definitions) {
    if (seenDefinitions.has(definition.definition_uid)) {
      continue;
    }
    seenDefinitions.add(definition.definition_uid);
    const key = foldKey(definition.term);
    if (key.length === 0) {
      continue;
    }
    const capitalised = UPPERCASE_START.test(definition.term.trim());
    for (const form of termForms(key)) {
      list.push({ kind: "definition", form, capitalised, definition });
    }
  }
  const byText = new Map<string, ReferenceRef[]>();
  for (const reference of references) {
    for (const text of referenceTexts(reference)) {
      const form = foldKey(text);
      if (form.length === 0) {
        continue;
      }
      const group = byText.get(form) ?? [];
      if (!group.some((item) => item.uid === reference.uid)) {
        group.push(reference);
      }
      byText.set(form, group);
    }
  }
  for (const [form, group] of byText) {
    list.push({ kind: "reference", form, references: group });
  }
  return list;
}

function indexByLeadingWord(list: Candidate[]): Map<string, Candidate[]> {
  const index = new Map<string, Candidate[]>();
  for (const candidate of list) {
    const word = leadingWord(candidate.form, 0);
    if (word.length === 0) {
      continue;
    }
    const bucket = index.get(word) ?? [];
    bucket.push(candidate);
    index.set(word, bucket);
  }
  for (const bucket of index.values()) {
    bucket.sort((left, right) => right.form.length - left.form.length || rank(left) - rank(right));
  }
  return index;
}

function rank(candidate: Candidate): number {
  return candidate.kind === "definition" ? 0 : 1;
}

function boundaryAfter(folded: string, end: number): boolean {
  if (isWordChar(folded[end])) {
    return false;
  }
  return !(folded[end] === "." && isWordChar(folded[end + 1]));
}

export function findTermMarks(
  text: string,
  definitions: readonly DefinitionRef[],
  references: readonly ReferenceRef[],
): TermMark[] {
  const index = indexByLeadingWord(candidates(definitions, references));
  if (index.size === 0 || text.length === 0) {
    return [];
  }
  const { folded, starts, ends } = foldText(text);
  const marks: TermMark[] = [];
  let position = 0;
  while (position < folded.length) {
    if (!isWordChar(folded[position]) || isWordChar(folded[position - 1])) {
      position += 1;
      continue;
    }
    const word = leadingWord(folded, position);
    const match = (index.get(word) ?? []).find((candidate) => {
      const end = position + candidate.form.length;
      if (!folded.startsWith(candidate.form, position) || !boundaryAfter(folded, end)) {
        return false;
      }
      if (candidate.kind === "definition" && candidate.capitalised) {
        return UPPERCASE_START.test(text.slice(starts[position] ?? 0));
      }
      return true;
    });
    if (match === undefined) {
      position += word.length;
      continue;
    }
    const last = position + match.form.length - 1;
    const start = starts[position] ?? 0;
    const end = ends[last] ?? text.length;
    const original = text.slice(start, end);
    marks.push(
      match.kind === "definition"
        ? { kind: "definition", start, end, text: original, definition: match.definition }
        : { kind: "reference", start, end, text: original, references: match.references },
    );
    position = last + 1;
  }
  return marks;
}

export type TermSegment = { kind: "text"; text: string; key: string } | (TermMark & { key: string });

export function segmentText(text: string, marks: readonly TermMark[]): TermSegment[] {
  const segments: TermSegment[] = [];
  let cursor = 0;
  for (const mark of marks) {
    if (mark.start > cursor) {
      segments.push({ kind: "text", text: text.slice(cursor, mark.start), key: `t${String(cursor)}` });
    }
    segments.push({ ...mark, key: `m${String(mark.start)}` });
    cursor = mark.end;
  }
  if (cursor < text.length) {
    segments.push({ kind: "text", text: text.slice(cursor), key: `t${String(cursor)}` });
  }
  return segments;
}
